import { toSafeString } from './toSafeString.js';
const UUID_REF_PATTERN = /^[0-9a-f]{8}-[0-9a-f-]{27}$/i;
const OPAQUE_LOWERCASE_REF_PATTERN = /^[a-z0-9]{14,}$/;

const toExplicitDisplayLabel = (value) => {
  return toSafeString(value) || null;
};

const toReadableWords = (value) => {
  return toSafeString(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._/-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const isOpaqueRefToken = (value) => {
  const normalized = toSafeString(value);

  if (!normalized) {
    return true;
  }

  if (UUID_REF_PATTERN.test(normalized)) {
    return true;
  }

  if (/[A-Z]/.test(normalized)) {
    return false;
  }

  return OPAQUE_LOWERCASE_REF_PATTERN.test(normalized);
};

const toReadableRefToken = (value) => {
  const normalized = toSafeString(value);

  if (!normalized) {
    return null;
  }

  const segments = normalized
    .split(':')
    .flatMap((part) => part.split('/'))
    .filter(Boolean);
  const leaf = segments.length > 0 ? segments[segments.length - 1] : normalized;

  if (isOpaqueRefToken(leaf)) {
    return null;
  }

  return toReadableWords(leaf) || null;
};

const toTitleWord = (word) => {
  if (/^[A-Z0-9]{2,4}$/.test(word)) {
    return word;
  }

  const normalized = toSafeString(word).toLowerCase();

  if (!normalized) {
    return '';
  }

  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
};

const toTitleCaseLabel = (value) => {
  const words = toReadableRefToken(value);

  if (!words) {
    return null;
  }

  return words
    .split(' ')
    .map((word) => toTitleWord(word))
    .join(' ');
};

export const resolvePieveMcpDisplayAppLabel = ({ value } = {}) => {
  return toTitleCaseLabel(value);
};

const toNormalizedExecuteAction = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const actionLabel = toExplicitDisplayLabel(value?.actionLabel) || toExplicitDisplayLabel(value?.actionName);
  const appLabel = toExplicitDisplayLabel(value?.appLabel) || toExplicitDisplayLabel(value?.appName);
  return {
    appRef: toSafeString(value?.appRef),
    actionRef: toSafeString(value?.actionRef),
    appLabel,
    actionLabel
  };
};

const toExecuteActions = (args = {}) => {
  const safeArgs = args && typeof args === 'object' && !Array.isArray(args) ? args : {};
  const actionItems = Array.isArray(safeArgs?.actions) ? safeArgs.actions.map((entry) => toNormalizedExecuteAction(entry)).filter(Boolean) : [];

  if (actionItems.length > 0) {
    return actionItems;
  }

  const singleAction = toNormalizedExecuteAction(safeArgs);

  if (!singleAction) {
    return [];
  }

  if (!singleAction.appRef && !singleAction.actionRef && !singleAction.actionLabel) {
    return [];
  }

  return [singleAction];
};

/**
 * Builds the running-form label for an MCP actions-execute tool call. When a single app is
 * shared across the call and every action has a display name, it uses the per-action forms:
 * one action -> "Running the {A} action in {App}"; two distinct -> "Running the {A1} and {A2}
 * actions in {App}"; the same action repeated -> "Running {N} {A} actions in {App}"; more than
 * two distinct -> "Running {A1}, {A2}, and {N} other actions in {App}". When action names or a
 * shared app are missing (multi-app or opaque ref), it falls back to the generic counted labels.
 * The completed form is derived downstream by the tense machinery swapping "Running" for "Ran".
 */
export const resolvePieveMcpActionsExecuteDisplayText = ({ args, variant = 'default' } = {}) => {
  const explicitAppLabel = toExplicitDisplayLabel(args?.appLabel) || toExplicitDisplayLabel(args?.appName);
  const explicitActionLabel = toExplicitDisplayLabel(args?.actionLabel) || toExplicitDisplayLabel(args?.actionName);
  const executeActions = toExecuteActions(args);

  if (executeActions.length < 1) {
    return 'Working in your app';
  }

  const sharedAppLabels = [
    ...new Set(
      executeActions
        .map((action) => {
          return toExplicitDisplayLabel(action?.appLabel) || toExplicitDisplayLabel(action?.appName) || resolvePieveMcpDisplayAppLabel({ value: action?.appRef });
        })
        .filter(Boolean)
    )
  ];
  const appLabel = explicitAppLabel || (sharedAppLabels.length === 1 ? sharedAppLabels[0] : null);
  const perActionLabels = executeActions.map((action) => {
    return toExplicitDisplayLabel(action?.actionLabel) || toExplicitDisplayLabel(action?.actionName);
  });
  const singleActionLabel = explicitActionLabel || perActionLabels[0];

  if (executeActions.length === 1 && variant === 'single_action_detail' && singleActionLabel) {
    return `Running ${singleActionLabel}`;
  }

  const hasEveryActionLabel = executeActions.length === 1 ? Boolean(singleActionLabel) : perActionLabels.every(Boolean);

  if (appLabel && hasEveryActionLabel) {
    if (executeActions.length === 1) {
      return `Running the ${singleActionLabel} action in ${appLabel}`;
    }

    const distinctActionLabels = [...new Set(perActionLabels)];

    if (distinctActionLabels.length === 1) {
      return `Running ${executeActions.length} ${distinctActionLabels[0]} actions in ${appLabel}`;
    }

    if (distinctActionLabels.length === 2) {
      return `Running the ${distinctActionLabels[0]} and ${distinctActionLabels[1]} actions in ${appLabel}`;
    }

    const otherActionCount = distinctActionLabels.length - 2;

    return `Running ${distinctActionLabels[0]}, ${distinctActionLabels[1]}, and ${otherActionCount} other ${otherActionCount === 1 ? 'action' : 'actions'} in ${appLabel}`;
  }

  if (executeActions.length === 1) {
    if (appLabel) {
      return `Working in ${appLabel}`;
    }

    return 'Working in your app';
  }

  const actionCount = executeActions.length;

  if (appLabel) {
    return `Taking ${actionCount} steps in ${appLabel}`;
  }

  return `Taking ${actionCount} steps in your apps`;
};
